import type { ExpenseDto, PolicyVersionDto } from '@glory/contracts';
import { formatDate, formatHours, formatMoney, t, tp } from '@glory/i18n';
import { type Kopecks, roundHalfUpToKopeck } from '@glory/kernel';
import {
  type IsoDate,
  monthlyAmount,
  overheadSummary,
  type OverheadSummary,
  PRICING_ENTITY,
  PRICING_ENTITY_ID,
} from '@glory/pricing/domain';
import {
  Badge,
  Button,
  Callout,
  Card,
  cn,
  ContentSwap,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  InfoHint,
  notify,
  PageHeader,
  SettingsSection,
  SkeletonRows,
  toastUndoable,
} from '@glory/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  CalendarX,
  Check,
  ChevronDown,
  MoreHorizontal,
  Pencil,
  Percent,
  Plus,
  Receipt,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';

import { ExpenseDialog } from '../../../features/pricing/expense-dialog.tsx';
import {
  expenseState,
  formatPolicyValue,
  POLICY_FIELD_LABEL,
  POLICY_FIELDS,
  type PolicyField,
  policyChanges,
  policySummary,
  sortExpenses,
  versionBefore,
} from '../../../features/pricing/model.ts';
import { PolicyDialog, type PolicyDialogMode } from '../../../features/pricing/policy-dialog.tsx';
import { CollapsedHistory, ScheduledVersion, VersionList } from '../../../features/pricing/version-parts.tsx';
import { client, orpc } from '../../../lib/orpc.ts';
import { requireSettingsPermission, usePermissions } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/settings/pricing')({
  beforeLoad: requireSettingsPermission('pricing.read'),
  component: PricingPage,
});

type OpenDialog =
  | { readonly kind: 'policy'; readonly mode: PolicyDialogMode }
  | { readonly kind: 'expense'; readonly expense: ExpenseDto | null };

const FIELD_HINTS = {
  overheadPerHour: { text: t('pricing.overheadHint'), example: t('pricing.overheadExample') },
  markup: { text: t('pricing.markupHint'), example: t('pricing.markupExample') },
  minMargin: { text: t('pricing.minMarginHint'), example: t('pricing.minMarginExample') },
  roundingStep: { text: t('pricing.roundingHint'), example: t('pricing.roundingExample') },
} as const satisfies Record<PolicyField, unknown>;

function PricingPage() {
  const { can } = usePermissions();
  const canRead = can('pricing.read');
  const canManage = can('pricing.manage');
  const policies = useQuery({ ...orpc.pricing.policies.list.queryOptions(), enabled: canRead });
  const expenses = useQuery({ ...orpc.pricing.expenses.list.queryOptions(), enabled: canRead });
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const close = () => {
    setDialog(null);
  };

  const today = policies.data?.today ?? expenses.data?.today ?? null;
  const versions = policies.data?.items ?? [];
  const current = versions.find((v) => v.status === 'current') ?? null;
  const summary =
    expenses.data && today ? overheadSummary(expenses.data.items, expenses.data.team, today) : null;
  // Загрузка, сбой и политика с расходами проявляются друг из друга, без скачка высоты.
  const phase = !canRead
    ? 'denied'
    : policies.isError || expenses.isError
      ? 'error'
      : !policies.data || !expenses.data || !today || !summary
        ? 'loading'
        : 'ready';

  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2 whitespace-normal">
            {t('pricing.title')}
            <InfoHint hint={{ text: t('pricing.hint'), example: t('pricing.hintExample') }} />
          </span>
        }
      />
      <p className="-mt-2 text-muted">{t('pricing.lead')}</p>

      <ContentSwap swapKey={phase} contentClassName="flex flex-col gap-4">
        {!canRead ? (
          <Callout tone="strong">{t('pricing.noAccess')}</Callout>
        ) : policies.isError || expenses.isError ? (
          <Callout>{(policies.error ?? expenses.error)?.message}</Callout>
        ) : !policies.data || !expenses.data || !today || !summary ? (
          <SkeletonRows rows={8} />
        ) : (
          <>
            <PolicySection versions={versions} current={current} canManage={canManage} onDialog={setDialog} />
            <ExpensesSection
              expenses={expenses.data.items}
              summary={summary}
              current={current}
              today={today}
              canManage={canManage}
              onDialog={setDialog}
            />
            {versions.length > 0 ? (
              <VersionList
                title={t('pricing.versionsTitle')}
                lead={t('pricing.versionsLead')}
                rows={versions.map((version) => ({ ...version, summary: policySummary(version) }))}
              />
            ) : null}
            <CollapsedHistory
              title={t('pricing.historyTitle')}
              lead={t('pricing.historyLead')}
              entityType={PRICING_ENTITY}
              entityId={PRICING_ENTITY_ID}
            />
          </>
        )}
      </ContentSwap>

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        {dialog?.kind === 'policy' && today ? (
          <PolicyDialog
            mode={dialog.mode}
            versions={versions}
            today={today}
            suggested={summary?.rate ?? null}
            onDone={close}
          />
        ) : null}
        {dialog?.kind === 'expense' && today ? (
          <ExpenseDialog expense={dialog.expense} today={today} onDone={close} />
        ) : null}
      </Dialog>
    </div>
  );
}

/** Запланированная версия политики: отличия от версии до её даты. */
function ScheduledPolicy({
  version,
  versions,
  canManage,
  onDialog,
}: {
  version: PolicyVersionDto;
  versions: readonly PolicyVersionDto[];
  canManage: boolean;
  onDialog: (dialog: OpenDialog) => void;
}) {
  const queryClient = useQueryClient();
  const previous = versionBefore(versions, version.effectiveFrom);
  return (
    <ScheduledVersion
      title={t('pricing.scheduledTitle', { date: formatDate(version.effectiveFrom) })}
      changes={policyChanges(previous, version)}
      hasPrevious={previous !== null}
      sameText={t('pricing.scheduledSame')}
      comment={version.comment}
      canManage={canManage}
      onEdit={() => {
        onDialog({ kind: 'policy', mode: { kind: 'scheduled', version } });
      }}
      onRemove={async () => {
        await client.pricing.policies.remove({ id: version.id });
        await queryClient.invalidateQueries({ queryKey: orpc.pricing.key() });
      }}
      removedText={t('pricing.scheduledRemoved', { date: formatDate(version.effectiveFrom) })}
    />
  );
}

function PolicySection({
  versions,
  current,
  canManage,
  onDialog,
}: {
  versions: readonly PolicyVersionDto[];
  current: PolicyVersionDto | null;
  canManage: boolean;
  onDialog: (dialog: OpenDialog) => void;
}) {
  const scheduled = versions.filter((v) => v.status === 'scheduled').reverse();
  const newVersion = canManage ? (
    <Button
      onClick={() => {
        onDialog({ kind: 'policy', mode: { kind: 'new' } });
      }}
    >
      <Plus /> {t('pricing.newVersion')}
    </Button>
  ) : null;

  if (!current) {
    return (
      <Card>
        <EmptyState
          icon={<Percent />}
          title={t('pricing.policyEmptyTitle')}
          description={canManage ? t('pricing.policyEmptyText') : t('pricing.policyEmptyReadOnly')}
          action={
            canManage ? (
              <Button
                variant="primary"
                onClick={() => {
                  onDialog({ kind: 'policy', mode: { kind: 'new' } });
                }}
              >
                {t('pricing.policyCreate')}
              </Button>
            ) : null
          }
        />
        {scheduled.length > 0 ? (
          <div className="flex flex-col gap-2 border-t border-border-soft px-4 py-3">
            {scheduled.map((version) => (
              <ScheduledPolicy
                key={version.id}
                version={version}
                versions={versions}
                canManage={canManage}
                onDialog={onDialog}
              />
            ))}
          </div>
        ) : null}
      </Card>
    );
  }

  return (
    <SettingsSection
      title={t('pricing.policyTitle')}
      lead={t('pricing.policySince', { date: formatDate(current.effectiveFrom) })}
      action={newVersion}
    >
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {POLICY_FIELDS.map((field) => (
          <div key={field} className="rounded-md bg-surface-2 px-2.5 py-2">
            <dt className="flex items-center gap-1 text-caption font-semibold text-muted">
              {POLICY_FIELD_LABEL[field]}
              <InfoHint
                hint={FIELD_HINTS[field]}
                label={t('fields.hintFor', { label: POLICY_FIELD_LABEL[field] })}
              />
            </dt>
            <dd className="font-display text-title font-bold tabular-nums">
              {formatPolicyValue(field, current)}
            </dd>
          </div>
        ))}
      </dl>
      {current.comment ? <p className="mt-2 text-small text-muted">«{current.comment}»</p> : null}
      {scheduled.length > 0 ? (
        <div className="mt-3 flex flex-col gap-2">
          {scheduled.map((version) => (
            <ScheduledPolicy
              key={version.id}
              version={version}
              versions={versions}
              canManage={canManage}
              onDialog={onDialog}
            />
          ))}
        </div>
      ) : null}
    </SettingsSection>
  );
}

function ExpensesSection({
  expenses,
  summary,
  current,
  today,
  canManage,
  onDialog,
}: {
  expenses: readonly ExpenseDto[];
  summary: OverheadSummary;
  current: PolicyVersionDto | null;
  today: IsoDate;
  canManage: boolean;
  onDialog: (dialog: OpenDialog) => void;
}) {
  const [showEnded, setShowEnded] = useState(false);
  const sorted = sortExpenses(expenses);
  const live = sorted.filter((e) => expenseState(e, today) !== 'ended');
  const ended = sorted.filter((e) => expenseState(e, today) === 'ended');
  const add = () => {
    onDialog({ kind: 'expense', expense: null });
  };

  return (
    <SettingsSection
      title={
        <span className="inline-flex items-center gap-2">
          {t('pricing.expensesTitle')}
          <InfoHint
            hint={{ text: t('pricing.expensesHint') }}
            label={t('fields.hintFor', { label: t('pricing.expensesTitle') })}
          />
        </span>
      }
      lead={t('pricing.expensesLead')}
      action={
        canManage && expenses.length > 0 ? (
          <Button onClick={add}>
            <Plus /> {t('pricing.addExpense')}
          </Button>
        ) : null
      }
    >
      {expenses.length === 0 ? (
        <EmptyState
          icon={<Receipt />}
          title={t('pricing.expensesEmptyTitle')}
          description={t('pricing.expensesEmptyText')}
          action={
            canManage ? (
              <Button variant="primary" onClick={add}>
                <Plus /> {t('pricing.addExpense')}
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {live.length > 0 ? (
            <ul className="-my-1 flex flex-col divide-y divide-border-soft">
              {live.map((expense) => (
                <ExpenseRow
                  key={expense.id}
                  expense={expense}
                  today={today}
                  canManage={canManage}
                  onDialog={onDialog}
                />
              ))}
            </ul>
          ) : null}
          {ended.length > 0 ? (
            <div className="flex flex-col gap-1">
              <button
                type="button"
                aria-expanded={showEnded}
                className="inline-flex items-center gap-1 self-start text-small font-semibold text-muted hover:text-text"
                onClick={() => {
                  setShowEnded((v) => !v);
                }}
              >
                <ChevronDown
                  aria-hidden
                  className={cn('size-4 transition-transform', showEnded && 'rotate-180')}
                />
                {tp('pricing.endedToggle', ended.length)}
              </button>
              {showEnded ? (
                <ul className="flex flex-col divide-y divide-border-soft opacity-70">
                  {ended.map((expense) => (
                    <ExpenseRow
                      key={expense.id}
                      expense={expense}
                      today={today}
                      canManage={canManage}
                      onDialog={onDialog}
                    />
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          <OverheadCalculation
            summary={summary}
            current={current}
            canManage={canManage}
            onDialog={onDialog}
          />
        </div>
      )}
    </SettingsSection>
  );
}

function ExpenseRow({
  expense,
  today,
  canManage,
  onDialog,
}: {
  expense: ExpenseDto;
  today: IsoDate;
  canManage: boolean;
  onDialog: (dialog: OpenDialog) => void;
}) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.pricing.expenses.key() });
  const state = expenseState(expense, today);
  const edit = () => {
    onDialog({ kind: 'expense', expense });
  };

  const remove = useMutation({
    mutationFn: () => client.pricing.expenses.remove({ id: expense.id, version: expense.version }),
    onSuccess: async () => {
      await refresh();
      toastUndoable({
        message: t('pricing.expenseDeleted', { name: expense.name }),
        onUndo: () =>
          void client.pricing.expenses
            .restore({ id: expense.id, version: expense.version + 1 })
            .then(refresh),
      });
    },
    onError: (error) => {
      notify(error.message);
    },
  });
  const end = useMutation({
    mutationFn: () =>
      client.pricing.expenses.update({
        id: expense.id,
        version: expense.version,
        name: expense.name,
        category: expense.category,
        amount: expense.amount,
        period: expense.period,
        activeFrom: expense.activeFrom,
        activeTo: today,
      }),
    onSuccess: async () => {
      notify(t('pricing.expenseEnded', { name: expense.name }));
      await refresh();
    },
    onError: (error) => {
      notify(error.message);
    },
  });

  const status =
    state === 'upcoming'
      ? t('pricing.upcoming', { date: formatDate(expense.activeFrom) })
      : state === 'ended'
        ? t('pricing.ended', { date: formatDate(expense.activeTo ?? expense.activeFrom) })
        : expense.activeTo
          ? t('pricing.until', { date: formatDate(expense.activeTo) })
          : null;

  const main = (
    <>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-semibold">{expense.name}</span>
        <span className="truncate text-caption text-muted">
          {t(`pricing.categories.${expense.category}`)}
        </span>
      </div>
      {status ? (
        <Badge tone={state === 'upcoming' ? 'warn' : 'muted'} className="hidden sm:inline-flex">
          {status}
        </Badge>
      ) : null}
      <div className="flex shrink-0 flex-col items-end">
        <span className="font-bold tabular-nums">
          {formatMoney(expense.amount)}{' '}
          <span className="text-small font-semibold text-muted">
            {expense.period === 'year' ? t('pricing.perYear') : t('pricing.perMonth')}
          </span>
        </span>
        {expense.period === 'year' ? (
          <span className="text-caption text-muted tabular-nums">
            {t('pricing.monthlyShare', {
              amount: formatMoney(roundHalfUpToKopeck(monthlyAmount(expense))),
            })}
          </span>
        ) : null}
        {status ? <span className="text-caption text-muted sm:hidden">{status}</span> : null}
      </div>
    </>
  );

  return (
    <li className="flex items-center gap-2 py-2">
      {canManage ? (
        <button
          type="button"
          className="-mx-1.5 flex min-w-0 flex-1 items-center gap-3 rounded-md px-1.5 py-0.5 text-left hover:bg-surface-2"
          onClick={edit}
        >
          {main}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3">{main}</div>
      )}
      {canManage ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton label={t('pricing.expenseMenu')} icon={<MoreHorizontal />} size="sm" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem icon={<Pencil />} onSelect={edit}>
              {t('pricing.editExpense')}
            </DropdownMenuItem>
            {state === 'active' && expense.activeTo === null ? (
              <DropdownMenuItem
                icon={<CalendarX />}
                onSelect={() => {
                  end.mutate();
                }}
              >
                {t('pricing.endExpense')}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              icon={<Trash2 />}
              onSelect={() => {
                remove.mutate();
              }}
            >
              {t('pricing.deleteExpense')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </li>
  );
}

/** Цепочка расчёта: расходы в месяц ÷ оплачиваемые часы = ставка; рядом — что в политике и «Применить». */
function OverheadCalculation({
  summary,
  current,
  canManage,
  onDialog,
}: {
  summary: OverheadSummary;
  current: PolicyVersionDto | null;
  canManage: boolean;
  onDialog: (dialog: OpenDialog) => void;
}) {
  const rate = summary.rate;
  const differs = rate !== null && current !== null && rate !== current.overheadPerHour;
  const apply = (value: Kopecks) => {
    onDialog({ kind: 'policy', mode: { kind: 'apply', overheadPerHour: value } });
  };

  return (
    <div className="flex flex-col gap-2 rounded-md bg-surface-2 px-3 py-2.5">
      <dl className="flex flex-col gap-1 text-small">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="flex flex-col">
            <span className="font-semibold">{t('pricing.calcExpenses')}</span>
            <span className="text-caption text-muted">
              {tp('pricing.calcExpensesCount', summary.expenses)}
            </span>
          </dt>
          <dd className="font-bold whitespace-nowrap tabular-nums">{formatMoney(summary.perMonth)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="flex flex-wrap items-center gap-x-1.5">
            <span aria-hidden className="text-muted">
              ÷
            </span>
            <span className="font-semibold">{t('pricing.calcHours')}</span>
            <InfoHint
              hint={{ text: t('pricing.calcHoursHint'), example: t('pricing.calcHoursExample') }}
              label={t('fields.hintFor', { label: t('pricing.calcHours') })}
            />
            <Link to="/team" className="text-caption font-semibold text-muted underline underline-offset-2">
              {t('pricing.calcHoursLink')}
            </Link>
          </dt>
          <dd className="font-bold whitespace-nowrap tabular-nums">
            {t('pricing.calcHoursValue', {
              hours: t('units.hoursShort', { value: formatHours(summary.minutesPerMonth) }),
            })}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 border-t border-border-soft pt-1.5">
          <dt className="flex items-center gap-1.5">
            <span aria-hidden className="text-muted">
              =
            </span>
            <span className="font-bold">{t('pricing.calcRate')}</span>
          </dt>
          <dd className="font-display text-title font-bold tabular-nums">
            {rate === null ? '—' : formatMoney(rate, { perHour: true })}
          </dd>
        </div>
      </dl>

      {rate === null ? (
        <Callout tone="strong" className="text-small">
          {t('pricing.calcNoHours')}
        </Callout>
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5">
          {current === null ? (
            <span className="text-small text-muted">{t('pricing.noPolicyYet')}</span>
          ) : differs ? (
            <span className="text-small text-muted">
              {t('pricing.inPolicy', { rate: formatMoney(current.overheadPerHour, { perHour: true }) })}
            </span>
          ) : (
            <Badge tone="neutral">
              <Check className="size-3" aria-hidden /> {t('pricing.matchesPolicy')}
            </Badge>
          )}
          {canManage && (differs || current === null) ? (
            <span className="inline-flex items-center gap-1.5">
              <InfoHint
                hint={{ text: t('pricing.applyHint') }}
                label={t('fields.hintFor', { label: t('pricing.apply') })}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  apply(rate);
                }}
              >
                {t('pricing.apply')}
              </Button>
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
